import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import Axios from "axios";
import { message, Spin, Modal, Tooltip } from "antd";
import {
  ArrowLeftOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckOutlined,
  CarOutlined,
  ShopOutlined,
  CopyOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  CloseOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { LogoBlack } from "../../assets/icons";
import LetterAvatar from "../../reusables/LetterAvatar";
import "./index.scss";

const MULTI_LEVEL_BASE_URL =
  process.env.REACT_APP_MULTI_LEVEL_BASE_URL || "http://localhost:4000";

// Shopify-inspired status styles
const SHOPIFY_STATUS_CONFIG = {
  pending: {
    label: "Order placed",
    deliveryLabel: "Order placed",
    className: "status-pending",
    desc: "Order has been received and confirmed by the merchant.",
  },
  paid: {
    label: "Payment confirmed",
    deliveryLabel: "Payment confirmed",
    className: "status-paid",
    desc: "Payment confirmed. Merchant is preparing your items.",
  },
  processing: {
    label: "In preparation",
    deliveryLabel: "In preparation",
    className: "status-processing",
    desc: "Merchant is packing and preparing your items.",
  },
  in_progress: {
    label: "In preparation",
    deliveryLabel: "In preparation",
    className: "status-processing",
    desc: "Merchant is packing and preparing your items.",
  },
  ready: {
    label: "Ready for pickup",
    deliveryLabel: "Out for delivery",
    className: "status-ready",
    desc: "Your order is ready or out for delivery.",
  },
  completed: {
    label: "Fulfilled",
    deliveryLabel: "Delivered",
    className: "status-completed",
    desc: "Order fulfilled and delivered. Thank you!",
  },
  delivered: {
    label: "Delivered",
    deliveryLabel: "Delivered",
    className: "status-delivered",
    desc: "Order has been delivered. Thank you!",
  },
  fulfilled: {
    label: "Fulfilled",
    deliveryLabel: "Delivered",
    className: "status-completed",
    desc: "Order fulfilled and delivered. Thank you!",
  },
  cancelled: {
    label: "Cancelled",
    deliveryLabel: "Cancelled",
    className: "status-cancelled",
    desc: "This order was cancelled.",
  },
  rejected: {
    label: "Rejected",
    deliveryLabel: "Rejected",
    className: "status-cancelled",
    desc: "This order was rejected.",
  },
};

const formatPrice = (val, currency = "₦") => {
  const num = Number(val) || 0;
  return `${currency}${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const Transactions = () => {
  const history = useHistory();
  const location = useLocation();

  // Active filter tab: "all", "active", "completed", "appointments"
  const [activeTab, setActiveTab] = useState("all");

  // Search input state
  const [searchInput, setSearchInput] = useState("");
  const [activeSearchFilter, setActiveSearchFilter] = useState("");

  // Data states
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [reservations, setReservations] = useState([]);

  // Detail Modal state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);

  // Hotel Reservation Modal state
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);

  // Authenticated user & merchant branding
  const [currentUser, setCurrentUser] = useState(null);
  const [merchantUrl, setMerchantUrl] = useState("/");
  const [merchantBrand, setMerchantBrand] = useState({
    name: "",
    logo: "",
    url: "",
  });

  // Read customer & merchant branding on mount
  useEffect(() => {
    document.title = "Orders & Tracking";
    try {
      const stored = localStorage.getItem("user_customer_details");
      if (stored) {
        const parsed = JSON.parse(stored);
        setCurrentUser(parsed);
      }

      // Check merchant details from local storage
      const storedMerchantStr =
        localStorage.getItem("merchant_details") ||
        localStorage.getItem("urewards_merchant_details");
      if (storedMerchantStr) {
        const m = JSON.parse(storedMerchantStr);
        const name = m.business_name || m.merchant_details?.business_name || "";
        const logo =
          m.image ||
          m.logo ||
          m.merchant_details?.image ||
          m.merchant_details?.logo ||
          "";
        const mId = m.id || m.merchant_details?.id;

        if (name || logo) {
          setMerchantBrand((prev) => ({
            name: name || prev.name,
            logo: logo || prev.logo,
            url: prev.url,
          }));
        }

        // Fetch merchant logo from backend if missing but ID exists
        if (mId && !logo) {
          Axios.get(`${MULTI_LEVEL_BASE_URL}/get_merchant_by_id/${mId}`)
            .then((res) => {
              if (res.data?.success && res.data.merchant) {
                setMerchantBrand((prev) => ({
                  ...prev,
                  name: res.data.merchant.business_name || prev.name,
                  logo: res.data.merchant.image || prev.logo,
                }));
              }
            })
            .catch(() => {});
        }
      }

      const mUrl = localStorage.getItem("urewards_merchant_url");
      if (mUrl) {
        const parsedUrl =
          typeof mUrl === "string" && mUrl.startsWith('"')
            ? JSON.parse(mUrl)
            : mUrl;
        setMerchantUrl(parsedUrl);
        setMerchantBrand((prev) => ({ ...prev, url: parsedUrl }));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // Fetch orders helper
  const fetchOrdersAndAppointments = useCallback(
    async (params) => {
      setLoading(true);
      try {
        const orderRes = await Axios.get(
          `${MULTI_LEVEL_BASE_URL}/customer_orders`,
          { params }
        );

        if (orderRes.data && orderRes.data.success) {
          const fetchedOrders = orderRes.data.data?.orders || [];
          setOrders(fetchedOrders);

          // Infer merchant branding from orders if not already resolved
          if (fetchedOrders.length > 0) {
            const first =
              fetchedOrders.find((o) => o.merchant_logo || o.merchant_name) ||
              fetchedOrders[0];
            if (first) {
              setMerchantBrand((prev) => ({
                name: prev.name || first.merchant_name || "",
                logo: prev.logo || first.merchant_logo || "",
                url: prev.url,
              }));
            }
          }

          // If tracking a specific order, open modal immediately
          if (params.order_id && fetchedOrders.length > 0) {
            setSelectedOrder(fetchedOrders[0]);
            setIsModalOpen(true);
          }
        } else {
          setOrders([]);
        }

        // Also fetch appointments if phone number is provided
        if (params.phone) {
          try {
            const aptRes = await Axios.get(
              `${MULTI_LEVEL_BASE_URL}/customer_appointments`,
              { params: { phone: params.phone } }
            );
            if (aptRes.data && aptRes.data.success) {
              setAppointments(aptRes.data.data?.appointments || []);
            }
          } catch (aptErr) {
            console.warn("Failed to load appointments:", aptErr);
          }
        }

        // Also fetch hotel room reservations
        try {
          const hotelParams = {};
          if (params.phone) hotelParams.phone = params.phone;
          if (params.email) hotelParams.email = params.email;
          if (params.order_id) hotelParams.confirmation_number = params.order_id;
          if (params.merchant_id) hotelParams.merchant_id = params.merchant_id;

          if (
            hotelParams.phone ||
            hotelParams.email ||
            hotelParams.confirmation_number
          ) {
            const hotelRes = await Axios.get(
              `${MULTI_LEVEL_BASE_URL}/customer_hotel_reservations`,
              { params: hotelParams }
            );
            if (hotelRes.data && hotelRes.data.success) {
              const fetchedReservations =
                hotelRes.data.data?.reservations || [];
              setReservations(fetchedReservations);

              // If searching by confirmation number, open reservation modal
              if (params.order_id && fetchedReservations.length > 0) {
                const matched = fetchedReservations.find(
                  (r) =>
                    r.confirmation_number?.toLowerCase() ===
                    params.order_id.toLowerCase()
                );
                if (matched) {
                  setSelectedReservation(matched);
                  setIsReservationModalOpen(true);
                  setActiveTab("reservations");
                }
              }
            }
          }
        } catch (hErr) {
          console.warn("Failed to load hotel reservations:", hErr);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        message.error(
          err.response?.data?.message || "Failed to load order history"
        );
        setOrders([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Initial load
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const orderIdParam = searchParams.get("order_id");
    const phoneParam = searchParams.get("phone");
    const emailParam = searchParams.get("email");

    if (orderIdParam) {
      setSearchInput(orderIdParam);
      setActiveSearchFilter(`Order #${orderIdParam}`);
      fetchOrdersAndAppointments({ order_id: orderIdParam });
    } else if (phoneParam) {
      setSearchInput(phoneParam);
      setActiveSearchFilter(`Phone: ${phoneParam}`);
      fetchOrdersAndAppointments({ phone: phoneParam });
    } else if (emailParam) {
      setSearchInput(emailParam);
      setActiveSearchFilter(`Email: ${emailParam}`);
      fetchOrdersAndAppointments({ email: emailParam });
    } else if (currentUser?.phone_number || currentUser?.email) {
      setActiveSearchFilter(
        `User: ${currentUser.first_name || ""} (${currentUser.phone_number || currentUser.email})`
      );
      fetchOrdersAndAppointments({
        phone: currentUser.phone_number,
        email: currentUser.email,
        customer_id: currentUser.user_id,
      });
    } else {
      try {
        const recentOrderStr = localStorage.getItem("ureward_orderDetails");
        if (recentOrderStr) {
          const recentOrder = JSON.parse(recentOrderStr);
          const recentId =
            recentOrder.order_id || recentOrder.id || recentOrder.reference;
          const recentPhone =
            recentOrder.customer_phone_number ||
            recentOrder.phone ||
            recentOrder.customer?.phone_number;

          if (recentId) {
            setSearchInput(recentId);
            setActiveSearchFilter(`Recent Order #${recentId}`);
            fetchOrdersAndAppointments({ order_id: recentId });
          } else if (recentPhone) {
            setSearchInput(recentPhone);
            setActiveSearchFilter(`Phone: ${recentPhone}`);
            fetchOrdersAndAppointments({ phone: recentPhone });
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [location.search, currentUser, fetchOrdersAndAppointments]);

  // Handle Search Submission
  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const query = (searchInput || "").trim();
    if (!query) {
      message.info("Please enter an Order ID, Phone number, or Email to track.");
      return;
    }

    const isEmail = query.includes("@");
    const isPhone = /^[+]?[\d\s\-()]{7,}$/.test(query);

    const params = {};
    if (isEmail) {
      params.email = query;
      setActiveSearchFilter(`Email: ${query}`);
    } else if (isPhone) {
      params.phone = query;
      setActiveSearchFilter(`Phone: ${query}`);
    } else {
      params.order_id = query;
      setActiveSearchFilter(`Order #${query}`);
    }

    fetchOrdersAndAppointments(params);
  };

  const handleClearFilter = () => {
    setActiveSearchFilter("");
    setSearchInput("");
    if (currentUser?.phone_number || currentUser?.email) {
      fetchOrdersAndAppointments({
        phone: currentUser.phone_number,
        email: currentUser.email,
      });
    } else {
      setOrders([]);
      setAppointments([]);
    }
  };

  // Refresh order live status
  const handleRefreshOrderStatus = async () => {
    if (!selectedOrder) return;
    const lookupId =
      selectedOrder.order_id || selectedOrder.id || selectedOrder.reference_code;
    setRefreshingStatus(true);
    try {
      const res = await Axios.get(
        `${MULTI_LEVEL_BASE_URL}/order_status/${lookupId}`
      );
      if (res.data && res.data.success && res.data.data) {
        setSelectedOrder(res.data.data);
        setOrders((prev) =>
          prev.map((ord) => (ord.id === res.data.data.id ? res.data.data : ord))
        );
        message.success("Order status refreshed");
      }
    } catch (err) {
      message.error("Could not refresh order status.");
    } finally {
      setRefreshingStatus(false);
    }
  };

  // Filter groups
  const activeOrders = orders.filter((ord) => {
    const st = String(ord.status || "").toLowerCase();
    return ["pending", "paid", "processing", "in_progress", "ready"].includes(st);
  });

  const completedOrders = orders.filter((ord) => {
    const st = String(ord.status || "").toLowerCase();
    return ["completed", "delivered", "fulfilled"].includes(st);
  });

  let displayOrders = orders;
  if (activeTab === "active") displayOrders = activeOrders;
  if (activeTab === "completed") displayOrders = completedOrders;

  // Active merchant branding resolution
  const activeMerchantName =
    merchantBrand.name ||
    selectedOrder?.merchant_name ||
    (orders.length > 0 ? orders[0].merchant_name : "") ||
    "Shop";

  const activeMerchantLogo =
    merchantBrand.logo ||
    selectedOrder?.merchant_logo ||
    (orders.length > 0 ? orders[0].merchant_logo : "");

  const merchantStoreLink = merchantBrand.url || merchantUrl || "/";

  // Copy helper
  const copyToClipboard = (text, label = "Order ID") => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      message.success(`${label} copied!`);
    }
  };

  return (
    <div className="ls-transactions-page">
      {/* Sticky Minimal Navigation with Merchant Branding */}
      <header className="ls-tx-topbar">
        <div className="ls-tx-topbar-inner">
          <button
            type="button"
            className="ls-tx-back-btn"
            onClick={() => {
              if (window.history.length > 2) {
                history.goBack();
              } else {
                history.push(merchantStoreLink);
              }
            }}
          >
            <ArrowLeftOutlined /> Back
          </button>

          <Link
            to={merchantStoreLink}
            className="ls-tx-brand"
            title={activeMerchantName}
          >
            {activeMerchantLogo ? (
              <img
                src={activeMerchantLogo}
                alt={activeMerchantName}
                className="ls-tx-brand-img"
              />
            ) : activeMerchantName && activeMerchantName !== "Shop" ? (
              <LetterAvatar name={activeMerchantName} size={32} radius={8} />
            ) : (
              <img src={LogoBlack} alt="Shop" className="ls-tx-brand-img" />
            )}
            <span className="ls-tx-brand-name">{activeMerchantName}</span>
          </Link>

          {currentUser ? (
            <div className="ls-tx-user-pill">
              <span className="ls-tx-avatar-dot">
                {(currentUser.first_name || "U").charAt(0).toUpperCase()}
              </span>
              <span>{currentUser.first_name || currentUser.phone_number}</span>
            </div>
          ) : (
            <Link to="/my" className="ls-tx-user-pill">
              <span>Sign In / My Pass</span>
            </Link>
          )}
        </div>
      </header>

      {/* Main Page Area */}
      <main className="ls-tx-container">
        {/* Header & Minimal Search Bar */}
        <section className="ls-tx-header-section">
          <div className="ls-tx-title-row">
            <div className="ls-tx-heading-block">
              <h1>Orders</h1>
              <p>Track shipments, live progress, and previous purchases</p>
            </div>
          </div>

          <form className="ls-tx-search-container" onSubmit={handleSearch}>
            <SearchOutlined className="ls-tx-search-icon" />
            <input
              type="text"
              className="ls-tx-search-input"
              placeholder="Search by order number (e.g. 0007), phone, or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button
              type="submit"
              className="ls-tx-search-btn"
              disabled={loading}
            >
              Track Order
            </button>
          </form>

          {activeSearchFilter && (
            <div className="ls-tx-filter-indicator">
              <span>Showing:</span>
              <span className="filter-badge">
                {activeSearchFilter}
                <button
                  type="button"
                  onClick={handleClearFilter}
                  title="Clear filter"
                >
                  <CloseOutlined style={{ fontSize: 10 }} />
                </button>
              </span>
            </div>
          )}
        </section>

        {/* Minimal Segmented Tabs */}
        <div className="ls-tx-segmented-tabs">
          <button
            type="button"
            className={`ls-tx-tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All orders
            <span
              className={`tab-count ${activeTab === "all" ? "active" : ""}`}
            >
              {orders.length}
            </span>
          </button>

          <button
            type="button"
            className={`ls-tx-tab-btn ${activeTab === "active" ? "active" : ""}`}
            onClick={() => setActiveTab("active")}
          >
            In progress
            <span
              className={`tab-count ${activeTab === "active" ? "active" : ""}`}
            >
              {activeOrders.length}
            </span>
          </button>

          <button
            type="button"
            className={`ls-tx-tab-btn ${activeTab === "completed" ? "active" : ""}`}
            onClick={() => setActiveTab("completed")}
          >
            Completed
            <span
              className={`tab-count ${activeTab === "completed" ? "active" : ""}`}
            >
              {completedOrders.length}
            </span>
          </button>

          {appointments.length > 0 && (
            <button
              type="button"
              className={`ls-tx-tab-btn ${
                activeTab === "appointments" ? "active" : ""
              }`}
              onClick={() => setActiveTab("appointments")}
            >
              Bookings
              <span
                className={`tab-count ${
                  activeTab === "appointments" ? "active" : ""
                }`}
              >
                {appointments.length}
              </span>
            </button>
          )}

          {reservations.length > 0 && (
            <button
              type="button"
              className={`ls-tx-tab-btn ${
                activeTab === "reservations" ? "active" : ""
              }`}
              onClick={() => setActiveTab("reservations")}
            >
              Hotel stays
              <span
                className={`tab-count ${
                  activeTab === "reservations" ? "active" : ""
                }`}
              >
                {reservations.length}
              </span>
            </button>
          )}
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <Spin size="large" />
            <p
              style={{
                marginTop: 18,
                fontSize: 14,
                color: "var(--ls-muted, #6b6b76)",
                fontWeight: 500,
              }}
            >
              Loading orders and live status...
            </p>
          </div>
        ) : activeTab === "appointments" ? (
          /* Bookings List */
          <div className="ls-tx-orders-list">
            {appointments.length === 0 ? (
              <div className="ls-tx-empty-state">
                <ShopOutlined className="empty-icon" />
                <h3>No Bookings</h3>
                <p>You haven't scheduled any service appointments yet.</p>
              </div>
            ) : (
              appointments.map((apt) => (
                <div key={apt.id} className="ls-tx-apt-card">
                  <div className="apt-details">
                    <h4>{apt.service_name || "Service Appointment"}</h4>
                    <div className="apt-meta">
                      <span>
                        {moment(apt.booking_date).format("MMM D, YYYY · h:mm A")}
                      </span>
                      {apt.branch_name && <span>· {apt.branch_name}</span>}
                      {apt.staff_name && <span>· Specialist: {apt.staff_name}</span>}
                    </div>
                  </div>
                  <div className="apt-right">
                    <span className="apt-price">
                      {formatPrice(apt.amount_charged)}
                    </span>
                    <span
                      className={`ls-shopify-badge ${
                        String(apt.status).toLowerCase() === "completed"
                          ? "status-completed"
                          : "status-pending"
                      }`}
                    >
                      <span className="badge-dot" />
                      {String(apt.status || "Assigned")}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : activeTab === "reservations" ? (
          /* Hotel Stays List */
          <div className="ls-tx-orders-list">
            {reservations.length === 0 ? (
              <div className="ls-tx-empty-state">
                <ShopOutlined className="empty-icon" />
                <h3>No Hotel Reservations Found</h3>
                <p>You haven't made any hotel room reservations yet.</p>
              </div>
            ) : (
              reservations.map((res) => {
                const status = String(res.status || "booked").toLowerCase();
                let badgeClass = "status-pending";
                if (status === "confirmed") badgeClass = "status-completed";
                else if (status === "checked_in") badgeClass = "status-ready";
                else if (status === "checked_out") badgeClass = "status-delivered";
                else if (status === "cancelled") badgeClass = "status-cancelled";

                return (
                  <article
                    key={res.id}
                    className="ls-tx-hotel-card"
                    onClick={() => {
                      setSelectedReservation(res);
                      setIsReservationModalOpen(true);
                    }}
                  >
                    {/* Top Row: Ref & Status */}
                    <div className="hotel-card-top">
                      <div className="hotel-ref-group">
                        <div className="hotel-conf-num">
                          <span>Reservation #{res.confirmation_number}</span>
                          <Tooltip title="Copy Confirmation Number">
                            <button
                              type="button"
                              className="ls-tx-copy-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(
                                  res.confirmation_number,
                                  "Confirmation #"
                                );
                              }}
                            >
                              <CopyOutlined />
                            </button>
                          </Tooltip>
                        </div>
                        <span className="hotel-booked-date">
                          Booked on{" "}
                          {moment(res.created_at).format("MMM D, YYYY")}
                        </span>
                      </div>

                      <span className={`ls-shopify-badge ${badgeClass}`}>
                        <span className="badge-dot" />
                        {res.status_label || res.status}
                      </span>
                    </div>

                    {/* Property Row */}
                    <div className="hotel-property-row">
                      {res.merchant_logo ? (
                        <img
                          src={res.merchant_logo}
                          alt={res.property_name}
                          className="hotel-logo-thumb"
                        />
                      ) : (
                        <LetterAvatar
                          name={res.property_name}
                          size={28}
                          radius={8}
                        />
                      )}
                      <span>{res.property_name}</span>
                    </div>

                    {/* Stay Details Grid */}
                    <div className="hotel-stay-details">
                      <div className="stay-detail-item">
                        <span className="stay-label">Check-in</span>
                        <span className="stay-value">
                          {moment(res.check_in_date).format("MMM D, YYYY")}
                        </span>
                      </div>

                      <div className="stay-detail-item">
                        <span className="stay-label">Check-out</span>
                        <span className="stay-value">
                          {moment(res.check_out_date).format("MMM D, YYYY")}
                        </span>
                      </div>

                      <div className="stay-detail-item">
                        <span className="stay-label">Duration</span>
                        <span className="stay-value">
                          {res.nights} {res.nights === 1 ? "Night" : "Nights"}
                        </span>
                      </div>

                      <div className="stay-detail-item">
                        <span className="stay-label">Guests</span>
                        <span className="stay-value">
                          {res.adults} {res.adults === 1 ? "Adult" : "Adults"}
                          {res.children > 0 ? `, ${res.children} Child` : ""}
                        </span>
                      </div>

                      <div
                        className="stay-detail-item"
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <span className="stay-label">Room Type</span>
                        <span className="stay-value">
                          {(res.rooms || [])
                            .map((r) => r.room_name)
                            .join(", ") || "Standard Room"}
                        </span>
                      </div>
                    </div>

                    {/* Bottom Row */}
                    <div className="hotel-card-bottom">
                      <div className="hotel-total-group">
                        <span className="total-label">Total Amount</span>
                        <span className="total-amount">
                          {formatPrice(
                            res.total_amount,
                            res.currency_code === "USD" ? "$" : "₦"
                          )}
                        </span>
                        {res.balance_due > 0 && (
                          <span className="balance-note">
                            Balance due:{" "}
                            {formatPrice(
                              res.balance_due,
                              res.currency_code === "USD" ? "$" : "₦"
                            )}
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        className="hotel-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReservation(res);
                          setIsReservationModalOpen(true);
                        }}
                      >
                        View stay details →
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        ) : (
          /* Shopify Orders List */
          <div className="ls-tx-orders-list">
            {displayOrders.length === 0 ? (
              <div className="ls-tx-empty-state">
                <ShopOutlined className="empty-icon" />
                <h3>No orders found</h3>
                <p>
                  {activeSearchFilter
                    ? `No orders matching "${activeSearchFilter}". Try checking your order number or phone number.`
                    : "You haven't placed any orders yet. Visit a merchant store to explore products!"}
                </p>
                <Link to={merchantUrl || "/"} className="empty-cta-btn">
                  Browse Store
                </Link>
              </div>
            ) : (
              displayOrders.map((order) => {
                const orderIdDisplay =
                  order.order_id || order.reference_code || order.id;
                const isDelivery = String(order.order_type || "")
                  .toLowerCase()
                  .includes("delivery");
                const rawStatus = String(order.status || "pending").toLowerCase();
                const statusMeta =
                  SHOPIFY_STATUS_CONFIG[rawStatus] ||
                  SHOPIFY_STATUS_CONFIG.pending;
                const statusLabel =
                  isDelivery && statusMeta.deliveryLabel
                    ? statusMeta.deliveryLabel
                    : statusMeta.label;

                const trackingTimeline = order.tracking;
                const currentStepIdx = trackingTimeline?.currentStep ?? 0;
                const isCancelled =
                  rawStatus === "cancelled" || rawStatus === "rejected";

                return (
                  <article
                    key={order.id}
                    className="ls-tx-card"
                    onClick={() => {
                      setSelectedOrder(order);
                      setIsModalOpen(true);
                    }}
                  >
                    {/* Top Row: Order # + Date & Status Badges */}
                    <div className="ls-tx-card-top">
                      <div className="ls-tx-card-title-group">
                        <div className="ls-tx-order-ref-row">
                          <span className="ls-tx-order-number">
                            Order #{orderIdDisplay}
                          </span>
                          <Tooltip title="Copy Order Reference">
                            <button
                              type="button"
                              className="ls-tx-copy-icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(orderIdDisplay, "Order ID");
                              }}
                            >
                              <CopyOutlined />
                            </button>
                          </Tooltip>
                        </div>
                        <span className="ls-tx-order-time">
                          Placed on{" "}
                          {moment(order.created_at).format("MMM D, YYYY · h:mm A")}
                        </span>
                      </div>

                      <div className="ls-tx-card-tags-group">
                        <span className="ls-shopify-type-pill">
                          {isDelivery ? <CarOutlined /> : <ShopOutlined />}
                          {isDelivery ? "Delivery" : "Store Pickup"}
                        </span>

                        <span
                          className={`ls-shopify-badge ${statusMeta.className}`}
                        >
                          <span className="badge-dot" />
                          {statusLabel}
                        </span>
                      </div>
                    </div>

                    {/* Store Row with Merchant Logo */}
                    <div className="ls-tx-card-store">
                      {order.merchant_logo ? (
                        <img
                          src={order.merchant_logo}
                          alt={order.merchant_name}
                          className="store-logo-thumb"
                        />
                      ) : order.merchant_name ? (
                        <LetterAvatar
                          name={order.merchant_name}
                          size={22}
                          radius={6}
                        />
                      ) : (
                        <ShopOutlined className="store-icon" />
                      )}
                      <span>{order.merchant_name || "Merchant Store"}</span>
                      {order.branch_id && (
                        <span className="branch-name">
                          · Branch #{order.branch_id}
                        </span>
                      )}
                    </div>

                    {/* Items List (Shopify Style) */}
                    <div className="ls-tx-card-items">
                      {(order.items || []).slice(0, 2).map((item, idx) => (
                        <div key={item.id || idx} className="ls-tx-line-item">
                          <div className="item-thumb-wrapper">
                            {item.picture ? (
                              <img
                                src={item.picture}
                                alt={item.name}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <LetterAvatar
                                name={item.name}
                                size={50}
                                radius={8}
                              />
                            )}
                          </div>
                          <div className="item-details">
                            <div className="item-name">{item.name}</div>
                            <div className="item-meta">
                              Qty {item.quantity || 1} ·{" "}
                              {formatPrice(item.price)}
                            </div>
                          </div>
                          <div className="item-price">
                            {formatPrice(item.amount || item.price * (item.quantity || 1))}
                          </div>
                        </div>
                      ))}

                      {(order.items || []).length > 2 && (
                        <div className="more-items-note">
                          + {(order.items || []).length - 2} more item
                          {(order.items || []).length - 2 > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>

                    {/* Milestone Progress Bar (Shopify Horizontal Track) */}
                    {!isCancelled && (
                      <div className="ls-tx-milestones">
                        <div className="milestones-header">
                          <span className="milestone-current-status">
                            {statusLabel}
                          </span>
                          <span className="milestone-step-count">
                            Step {currentStepIdx + 1} of 4
                          </span>
                        </div>
                        <div className="milestones-track">
                          <div className="track-connecting-line">
                            <div
                              className="track-connecting-fill"
                              style={{
                                width: `${(currentStepIdx / 3) * 100}%`,
                              }}
                            />
                          </div>

                          {[
                            { label: "Placed" },
                            { label: "Preparing" },
                            { label: isDelivery ? "Out for delivery" : "Ready" },
                            { label: isDelivery ? "Delivered" : "Completed" },
                          ].map((node, nodeIdx) => {
                            let nodeState = "upcoming";
                            if (nodeIdx < currentStepIdx) nodeState = "completed";
                            else if (nodeIdx === currentStepIdx) nodeState = "current";

                            return (
                              <div key={node.label} className="track-node">
                                <div className={`node-bullet ${nodeState}`} />
                                <span
                                  className={`node-label ${
                                    nodeIdx <= currentStepIdx ? "active" : ""
                                  }`}
                                >
                                  {node.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Card Bottom Row: Total & Action */}
                    <div className="ls-tx-card-bottom">
                      <div className="total-group">
                        <span className="total-label">Total</span>
                        <span className="total-amount">
                          {formatPrice(order.total)}
                        </span>
                      </div>

                      <div className="action-group">
                        <button
                          type="button"
                          className="ls-tx-btn-track"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                            setIsModalOpen(true);
                          }}
                        >
                          Track status →
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Shopify Shop App Style Modal */}
      {selectedOrder && (
        <Modal
          visible={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={600}
          className="ls-shopify-modal"
          centered
          destroyOnClose
        >
          {/* Header */}
          <div className="ls-modal-header">
            <div>
              <h3>
                Order #
                {selectedOrder.order_id ||
                  selectedOrder.reference_code ||
                  selectedOrder.id}
              </h3>
              <span className="modal-subtitle">
                {moment(selectedOrder.created_at).format("MMM D, YYYY · h:mm A")}
              </span>
            </div>

            <button
              type="button"
              className="ls-refresh-pill"
              onClick={handleRefreshOrderStatus}
              disabled={refreshingStatus}
            >
              <ReloadOutlined spin={refreshingStatus} /> Refresh
            </button>
          </div>

          <div style={{ marginTop: 22 }}>
            {/* Status Headline Banner */}
            {(() => {
              const rawSt = String(
                selectedOrder.status || "pending"
              ).toLowerCase();
              const isDeliv = String(selectedOrder.order_type || "")
                .toLowerCase()
                .includes("delivery");
              const meta =
                SHOPIFY_STATUS_CONFIG[rawSt] || SHOPIFY_STATUS_CONFIG.pending;
              const title =
                isDeliv && meta.deliveryLabel ? meta.deliveryLabel : meta.label;
              const isCancelled = rawSt === "cancelled" || rawSt === "rejected";

              return (
                <div
                  className="ls-modal-status-card"
                  style={{
                    backgroundColor: isCancelled ? "#fdebeb" : "#e4f8e9",
                    color: isCancelled ? "#bf0711" : "#108043",
                  }}
                >
                  <div className="status-icon">
                    {isCancelled ? <CloseOutlined /> : <CheckOutlined />}
                  </div>
                  <div className="status-text">
                    <h4>{title}</h4>
                    <p>
                      {selectedOrder.tracking?.statusDescription || meta.desc}
                    </p>
                  </div>
                </div>
              );
            })()}

            {/* Vertical Milestone Stepper (Shop App style) */}
            <div className="ls-modal-timeline">
              {(selectedOrder.tracking?.steps || [
                {
                  step: 0,
                  id: "placed",
                  title: "Order placed",
                  description: "Order received by merchant",
                },
                {
                  step: 1,
                  id: "processing",
                  title: "In preparation",
                  description: "Packed & verified",
                },
                {
                  step: 2,
                  id: "ready",
                  title: "Out for delivery / Ready",
                  description: "On its way to you",
                },
                {
                  step: 3,
                  id: "completed",
                  title: "Delivered",
                  description: "Order completed",
                },
              ]).map((stepItem, idx) => {
                const currentIdx = selectedOrder.tracking?.currentStep ?? 0;
                const isCancelled =
                  String(selectedOrder.status).toLowerCase() === "cancelled";

                let bubbleClass = "upcoming";
                if (isCancelled) {
                  bubbleClass = "cancelled";
                } else if (idx < currentIdx) {
                  bubbleClass = "completed";
                } else if (idx === currentIdx) {
                  bubbleClass = "current";
                }

                return (
                  <div key={stepItem.id || idx} className="timeline-step">
                    <div className="node-col">
                      <div className={`node-bubble ${bubbleClass}`}>
                        {bubbleClass === "completed" ? (
                          <CheckOutlined style={{ fontSize: 10 }} />
                        ) : bubbleClass === "cancelled" ? (
                          <CloseOutlined style={{ fontSize: 10 }} />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <div
                        className={`connector-line ${
                          idx < currentIdx ? "completed" : ""
                        }`}
                      />
                    </div>
                    <div className="content-col">
                      <h5 className="step-title">{stepItem.title}</h5>
                      <p className="step-desc">{stepItem.description}</p>
                      {idx === 0 && (
                        <div className="step-time">
                          {moment(selectedOrder.created_at).format("h:mm A")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Delivery Destination */}
            <div className="ls-modal-section">
              <div className="section-title">
                <EnvironmentOutlined />{" "}
                {String(selectedOrder.order_type || "")
                  .toLowerCase()
                  .includes("delivery")
                  ? "Delivery address"
                  : "Pickup location"}
              </div>

              <div className="section-row">
                <span>Customer</span>
                <strong>
                  {selectedOrder.customer?.name ||
                    selectedOrder.customer_name ||
                    "Customer"}
                </strong>
              </div>

              {(selectedOrder.customer?.phone ||
                selectedOrder.customer_phone_number) && (
                <div className="section-row">
                  <span>Phone</span>
                  <span>
                    {selectedOrder.customer?.phone ||
                      selectedOrder.customer_phone_number}
                  </span>
                </div>
              )}

              <div className="section-row">
                <span>Address / Store</span>
                <span>
                  {selectedOrder.customer?.delivery_address ||
                    selectedOrder.customer_delivery_address ||
                    selectedOrder.merchant_name ||
                    "HQ"}
                </span>
              </div>

              {selectedOrder.order_notes && (
                <div className="section-row">
                  <span>Instructions</span>
                  <span style={{ fontStyle: "italic" }}>
                    "{selectedOrder.order_notes}"
                  </span>
                </div>
              )}
            </div>

            {/* Line Items Breakdown */}
            <div className="ls-modal-section">
              <div className="section-title">
                <ShopOutlined /> Order items (
                {(selectedOrder.items || []).length})
              </div>

              {(selectedOrder.items || []).map((item, idx) => (
                <div
                  key={item.id || idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: "1px solid var(--ls-border, #e8e8ee)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flex: 1,
                    }}
                  >
                    {item.picture ? (
                      <img
                        src={item.picture}
                        alt={item.name}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 8,
                          objectFit: "cover",
                          border: "1px solid var(--ls-border, #e8e8ee)",
                        }}
                      />
                    ) : (
                      <LetterAvatar name={item.name} size={44} radius={8} />
                    )}
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "var(--ls-ink, #0f0f10)",
                        }}
                      >
                        {item.name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--ls-muted, #6b6b76)",
                          marginTop: 2,
                        }}
                      >
                        Qty {item.quantity || 1} · {formatPrice(item.price)}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "var(--ls-ink, #0f0f10)",
                    }}
                  >
                    {formatPrice(item.amount || item.price * item.quantity)}
                  </div>
                </div>
              ))}

              <div className="section-row bold-row">
                <span>Total</span>
                <span>{formatPrice(selectedOrder.total)}</span>
              </div>

              {selectedOrder.payment_receipt_url && (
                <div style={{ marginTop: 12 }}>
                  <a
                    href={selectedOrder.payment_receipt_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="receipt-pill-btn"
                  >
                    <FileTextOutlined /> View payment receipt
                  </a>
                </div>
              )}
            </div>

            {/* Merchant Contact */}
            <div className="ls-modal-section">
              <div className="section-title">
                <ShopOutlined /> Merchant
              </div>

              <div className="section-row">
                <span>Store</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {selectedOrder.merchant_logo ? (
                    <img
                      src={selectedOrder.merchant_logo}
                      alt={selectedOrder.merchant_name}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        objectFit: "cover",
                        border: "1px solid var(--ls-border, #e8e8ee)",
                      }}
                    />
                  ) : selectedOrder.merchant_name ? (
                    <LetterAvatar
                      name={selectedOrder.merchant_name}
                      size={24}
                      radius={6}
                    />
                  ) : null}
                  <strong>{selectedOrder.merchant_name}</strong>
                </div>
              </div>

              {selectedOrder.merchant_phone && (
                <div className="section-row">
                  <span>Phone</span>
                  <a
                    href={`tel:${selectedOrder.merchant_phone}`}
                    style={{
                      color: "var(--ls-ink, #0f0f10)",
                      fontWeight: 700,
                    }}
                  >
                    <PhoneOutlined /> {selectedOrder.merchant_phone}
                  </a>
                </div>
              )}

              {selectedOrder.merchant_email && (
                <div className="section-row">
                  <span>Email</span>
                  <a
                    href={`mailto:${selectedOrder.merchant_email}`}
                    style={{ color: "var(--ls-muted, #6b6b76)" }}
                  >
                    <MailOutlined /> {selectedOrder.merchant_email}
                  </a>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Hotel Reservation Details Modal */}
      {selectedReservation && (
        <Modal
          visible={isReservationModalOpen}
          onCancel={() => setIsReservationModalOpen(false)}
          footer={null}
          width={600}
          className="ls-shopify-modal"
          centered
          destroyOnClose
        >
          {/* Header */}
          <div className="ls-modal-header">
            <div>
              <h3>
                Reservation #{selectedReservation.confirmation_number}
              </h3>
              <span className="modal-subtitle">
                Booked on{" "}
                {moment(selectedReservation.created_at).format(
                  "MMM D, YYYY · h:mm A"
                )}
              </span>
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            {/* Status Headline Banner */}
            <div
              className="ls-modal-status-card"
              style={{
                backgroundColor:
                  String(selectedReservation.status).toLowerCase() ===
                  "cancelled"
                    ? "#fdebeb"
                    : "#e4f8e9",
                color:
                  String(selectedReservation.status).toLowerCase() ===
                  "cancelled"
                    ? "#bf0711"
                    : "#108043",
              }}
            >
              <div className="status-icon">
                {String(selectedReservation.status).toLowerCase() ===
                "cancelled" ? (
                  <CloseOutlined />
                ) : (
                  <CheckOutlined />
                )}
              </div>
              <div className="status-text">
                <h4>
                  {selectedReservation.status_label || selectedReservation.status}
                </h4>
                <p>
                  {selectedReservation.status === "checked_in"
                    ? "Guest is currently checked in at the property."
                    : selectedReservation.status === "confirmed"
                    ? "Reservation is confirmed by the property."
                    : selectedReservation.status === "checked_out"
                    ? "Stay completed and checked out. Thank you!"
                    : "Reservation has been booked."}
                </p>
              </div>
            </div>

            {/* Property Information */}
            <div className="ls-modal-section">
              <div className="section-title">
                <ShopOutlined /> Hotel Property
              </div>
              <div className="section-row">
                <span>Property</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {selectedReservation.merchant_logo ? (
                    <img
                      src={selectedReservation.merchant_logo}
                      alt={selectedReservation.property_name}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        objectFit: "cover",
                        border: "1px solid var(--ls-border, #e8e8ee)",
                      }}
                    />
                  ) : (
                    <LetterAvatar
                      name={selectedReservation.property_name}
                      size={24}
                      radius={6}
                    />
                  )}
                  <strong>{selectedReservation.property_name}</strong>
                </div>
              </div>
            </div>

            {/* Stay Dates & Rooms */}
            <div className="ls-modal-section">
              <div className="section-title">
                <EnvironmentOutlined /> Stay Details
              </div>

              <div className="section-row">
                <span>Check-in</span>
                <strong>
                  {moment(selectedReservation.check_in_date).format(
                    "dddd, MMMM D, YYYY"
                  )}
                </strong>
              </div>

              <div className="section-row">
                <span>Check-out</span>
                <strong>
                  {moment(selectedReservation.check_out_date).format(
                    "dddd, MMMM D, YYYY"
                  )}
                </strong>
              </div>

              <div className="section-row">
                <span>Duration</span>
                <span>
                  {selectedReservation.nights}{" "}
                  {selectedReservation.nights === 1 ? "Night" : "Nights"}
                </span>
              </div>

              <div className="section-row">
                <span>Guests</span>
                <span>
                  {selectedReservation.adults} Adults
                  {selectedReservation.children > 0
                    ? `, ${selectedReservation.children} Children`
                    : ""}
                </span>
              </div>

              {(selectedReservation.rooms || []).map((rm, idx) => (
                <div key={idx} className="section-row" style={{ marginTop: 6 }}>
                  <span>Room {idx + 1}</span>
                  <strong>
                    {rm.room_name} (
                    {formatPrice(
                      rm.rate_per_night,
                      selectedReservation.currency_code === "USD" ? "$" : "₦"
                    )}
                    /night)
                  </strong>
                </div>
              ))}

              {selectedReservation.special_requests && (
                <div className="section-row" style={{ marginTop: 6 }}>
                  <span>Special Requests</span>
                  <span style={{ fontStyle: "italic" }}>
                    "{selectedReservation.special_requests}"
                  </span>
                </div>
              )}
            </div>

            {/* Guest Details */}
            <div className="ls-modal-section">
              <div className="section-title">
                <EnvironmentOutlined /> Primary Guest
              </div>

              <div className="section-row">
                <span>Guest Name</span>
                <strong>
                  {selectedReservation.guest?.name || "Guest"}
                </strong>
              </div>

              {selectedReservation.guest?.phone && (
                <div className="section-row">
                  <span>Phone</span>
                  <span>{selectedReservation.guest.phone}</span>
                </div>
              )}

              {selectedReservation.guest?.email && (
                <div className="section-row">
                  <span>Email</span>
                  <span>{selectedReservation.guest.email}</span>
                </div>
              )}
            </div>

            {/* Financial Summary */}
            <div className="ls-modal-section">
              <div className="section-title">
                <ShopOutlined /> Payment Summary
              </div>

              <div className="section-row">
                <span>Total Amount</span>
                <strong>
                  {formatPrice(
                    selectedReservation.total_amount,
                    selectedReservation.currency_code === "USD" ? "$" : "₦"
                  )}
                </strong>
              </div>

              <div className="section-row">
                <span>Paid Amount</span>
                <span>
                  {formatPrice(
                    selectedReservation.paid_amount,
                    selectedReservation.currency_code === "USD" ? "$" : "₦"
                  )}
                </span>
              </div>

              <div className="section-row bold-row">
                <span>Balance Due</span>
                <span>
                  {formatPrice(
                    selectedReservation.balance_due,
                    selectedReservation.currency_code === "USD" ? "$" : "₦"
                  )}
                </span>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Transactions;
